import { Href, useRouter } from 'expo-router';
import { useEffect } from 'react';

import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { ROUTES } from '@/constants/routes';
import { useAuth } from '@/hooks/useAuth';
import { fetchAuthSession, signInWithSmartToken, signOutLocal } from '@/services/auth';
import { segmentClient } from '@/util/analytics';
import { trackAuthCallbackError } from '@/util/analyticsEvents';
import { parseSmartFragment, takeBinding } from '@/util/smartLaunch';

type SmartCallback = { code: string; sub: string; binding: string | null } | null;

let pendingCallback: SmartCallback | undefined;

let hasStartedSignIn = false;

/**
 * Takes the login token and binding once per page load, and holds them in this module so they
 * outlive the screen. Expo Router copies the URL fragment into its own state when it starts
 * and writes it back to the address bar on every sync, so clearing `location.hash` does not
 * hold; the screen navigates to the bare path instead, which gives the router a state without
 * the fragment and may remount this screen.
 */
function takeSmartCallback(): SmartCallback {
  if (pendingCallback !== undefined) {
    return pendingCallback;
  }

  const fragment = typeof window === 'undefined' ? null : parseSmartFragment(window.location.hash);

  pendingCallback = fragment ? { ...fragment, binding: takeBinding() } : null;

  return pendingCallback;
}

/** Test seam: each test is a fresh page load. */
export function resetSmartCallbackForTests() {
  pendingCallback = undefined;

  hasStartedSignIn = false;
}

/**
 * SMART EHR sign-in (/auth/smart).
 *
 * The API lands here with `#code={loginToken}&u={cognitoSub}`. The token is single-use and
 * lives 120 seconds; redeeming it needs the binding this tab stored at launch. Any session
 * already in the browser is signed out first, because EHR workstations are shared.
 */
export default function SmartCallbackScreen() {
  const { replace } = useRouter();

  const { loadCurrentUser } = useAuth();

  useEffect(() => {
    if (hasStartedSignIn) {
      return;
    }

    hasStartedSignIn = true;

    const callback = takeSmartCallback();

    if (typeof window !== 'undefined' && window.location.hash) {
      replace(ROUTES.SMART_CALLBACK as Href);
    }

    const fail = (reason: string, error?: unknown) => {
      trackAuthCallbackError({
        method: 'smart_ehr',
        source: 'smart_callback',
        reason,
        error,
      });

      replace(`${ROUTES.SMART_ERROR}?reason=expired` as Href);
    };

    if (!callback) {
      fail('missing_params');

      return;
    }

    const { code, sub, binding } = callback;

    if (!binding) {
      fail('missing_binding');

      return;
    }

    const run = async () => {
      try {
        const session = await fetchAuthSession();

        if (session.tokens) {
          await signOutLocal();
        }
      } catch (error) {
        console.debug('No active session or sign-out failed — proceeding with sign-in', error);
      }

      try {
        const result = await signInWithSmartToken(sub, code, binding);

        if (!result.isSignedIn) {
          fail('challenge_not_verified');

          return;
        }

        segmentClient.track(ANALYTICS_EVENTS.LOG_IN, {
          method: 'smart_ehr',
          source: 'smart_callback',
        });

        await loadCurrentUser();

        replace(ROUTES.INQUIRY as Href);
      } catch (error) {
        fail('auth_error', error);
      }
    };

    run();
  }, [loadCurrentUser, replace]);

  return (
    <VStack className="flex-1 items-center justify-center gap-6 p-6">
      <Spinner size="large" />
      <Text className="text-center font-semibold text-xl">Signing in…</Text>
    </VStack>
  );
}
